import { project } from '@/lib/project';
import {
	deDE,
	enUS,
	esES,
	esMX,
	frFR,
	itIT,
	plPL,
	ptBR,
	ptPT,
} from '@clerk/localizations';
import { ClerkProvider, SignIn, UserButton, useUser } from '@clerk/nextjs';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useState } from 'react';
import { useSessionStorage, useTimeout } from 'usehooks-ts';

const BottomDialog = dynamic(() =>
	import('@/components/ui/BottomDialog', { ssr: false })
);

export const CLERK_LOCALES = {
	en: enUS,
	pt: ptPT,
	it: itIT,
	pl: plPL,
	fr: frFR,
	de: deDE,
	es: esES,
	'en-GB': enUS,
	'en-CA': enUS,
	'es-MX': esMX,
	'pt-BR': ptBR,
	'en-ZA': enUS,
};

export default function AuthSignInSignOut({ showAuthDialog }) {
	const router = useRouter();
	const redirectUrl = `https://${project.domain}/${router.locale}${router.asPath}`;

	return (
		<ClerkProvider
			localization={CLERK_LOCALES[router.locale]}
			signInForceRedirectUrl={redirectUrl}
			signUpForceRedirectUrl={redirectUrl}
			forceRedirectUrl={redirectUrl}
			afterSignOutUrl={redirectUrl}
		>
			<AuthUser defaultOpen={showAuthDialog} />
		</ClerkProvider>
	);
}

export function AuthUser({ defaultOpen }) {
	const { user, isLoaded } = useUser();

	const [isAuthDialogDismissed, setIsAuthDialogDismissed] = useSessionStorage(
		'authDialogDismiss',
		false
	);

	const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

	const handleDialogClose = () => {
		setIsAuthDialogOpen(false);
		if (defaultOpen) {
			setIsAuthDialogDismissed(true);
		}
	};

	// Open after 3 second
	useTimeout(() => {
		if (defaultOpen && !isAuthDialogDismissed && !user) {
			setIsAuthDialogOpen(true);
		}
	}, 5000);

	if (!isLoaded) {
		return (
			<UserCircleIcon className="h-9 w-9 animate-pulse cursor-pointer text-gray-500 hover:text-gray-700 lg:h-8 lg:w-8" />
		);
	}

	return user ? (
		<>
			<Script id="bing-enhanced-conversion">
				{`window.uetq=window.uetq||[];
					window.uetq.push('set', { 'pid': {
						'em': "${user.primaryEmailAddress.emailAddress}",
						'ph': "",
					}});
				`}
				;
			</Script>
			<UserButton />
		</>
	) : (
		<>
			<button
				aria-label="Sign in button"
				onClick={() => setIsAuthDialogOpen(true)}
			>
				<UserCircleIcon className="h-9 w-9 cursor-pointer text-gray-500 hover:text-gray-700 lg:h-8 lg:w-8" />
			</button>
			<BottomDialog
				className={'md:max-w-md'}
				isOpen={isAuthDialogOpen}
				onClose={handleDialogClose}
			>
				<SignIn
					routing="hash"
					appearance={{
						elements: {
							rootBox: {
								marginInline: 'auto',
							},
							cardBox: {
								boxShadow: 'none',
							},
							card: {
								borderRadius: '0',
								paddingInline: 8,
								paddingBlock: 0,
							},
							footer: {
								display: 'none',
							},
						},
					}}
				/>
			</BottomDialog>
		</>
	);
}
